/* eslint-disable-file @typescript-eslint/no-unused-vars */
import './Dashboard.scss'
import React, { useContext } from 'react';
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { GoabBadge, GoabBlock, GoabButton, GoabCircularProgress, GoabDropdown, GoabDropdownItem, GoabFormItem, GoabIcon, GoabIconButton, GoabPagination, GoabSpacer, GoabTable, GoabTableSortHeader } from "@abgov/react-components"
import type { GoabCalloutType, GoabDropdownOnChangeDetail, GoabPaginationOnChangeDetail, GoabTableOnSortDetail, GoabTableSortDirection, GoabTemporaryNotificationType } from '@abgov/ui-components-common';
import { TemporaryNotification } from '@abgov/ui-components-common';
import { MultiSelect } from "react-multi-select-component";
import { Link, useNavigate } from "react-router-dom";
import TextField from '@mui/material/TextField';
import { useAuth } from 'react-oidc-context';
//import { AppContext } from '../../common/components/contexts/apiContext';
import type { GenListType, TicketFiltersInfo, TicketsQueryParam } from '../../common/types';
import type { TicketInfo } from '../../common/models/TicketInfo';
import ApiService from '../../sevices/ApiService';
import { wdpUtil } from '../../common/utils/util';
import { appLog } from '../../common/utils/app-log';
import { CustomFmF } from '../../common/components/datePicker/datePicker';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { dateFormat, tz, TicketStatuses } from '../../common/constants';
import type { TicketStatusLookupKey } from '../../common/constants';
import { AppContext } from '../../common/contexts/apiContext';
import { canShowButton, getSearchableStatusLookupKeys, getTicketRole, getViewableStatusLookupKeys } from '../ticketCtrl/ticketDetails/role-processor/permission-config';
import type { Role } from '../ticketCtrl/ticketDetails/role-processor/types';
import { TextMultiAutocomplete } from '../../common/components/TextMultiAutocomplete';
import { createButtonTestId, createTableTestId, createSearchTestId, createPageTestId, createFilterTestId } from '../../common/utils/testIds';
import { arePrerequisitesReady } from '../../common/utils/lookupReadiness';


const defSortDir: GoabTableSortDirection = 'desc';
const defSortCol = 'date';

const minSearchChars = 3;


dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault(tz);


export const Dashboard = (props: any) => {

  // generic
  const navigate = useNavigate();
  const [abortControllers, setAbortControllers] = useState<AbortController[]>([]);
  // Full-page blocking spinner (GoabCircularProgress) - used for the initial data load AND for every
  // backend call in flight (flag toggle/etc.) - set it right before the call and clear it in the .finally().
  const [progressMsg, setProgressMsg] = useState("");
  const [isInitialDataLoaded, setIsInitialDataLoaded] = useState(false);

  // authentication object
  const auth = useAuth();

  // main dataset coming from the backend
  const [mainTsList, setMainTsList] = useState(Array<TicketInfo>);
  const mainTsListCount = useRef(-1);


  // sorting and pagination
  const sorting = useRef<{ by: string, dir: GoabTableSortDirection }>({ by: defSortCol, dir: defSortDir });
  const pageNo = useRef(0);  //"0 instead of 1" this is workaround  to fix pagination pageNo dropdown not being populate first time
  const perPage = useRef(10);

  // Search and filters
  const searchStr = useRef('');
  const previewTableWrapElement = useRef<HTMLDivElement | null>(null);
  const searchWrap = useRef<HTMLDivElement | null>(null);
  const filters = useRef<TicketFiltersInfo>({ contractorNames: [], equipmentIds: [], statusIds: [], forestAreaIds: [], fireNos: [], dateFrom: null, dateTo: null });
  const previewTsListCount = useRef(-1);
  const [previewTsList, setPreviewTsList] = useState<Array<TicketInfo>>([]);

  // Fire number autocomplete state
  const [fireNumberInputValue, setFireNumberInputValue] = useState('');
  const [fireNumberOptions, setFireNumberOptions] = useState<GenListType[]>([]);

  // static Data
  // contractor names list
  //const [contractorNameValue, setContractorNameValue] = useState<GenListType | null>(null);
  //const [contractorNameInputValue, setContractorNameInputValue] = useState('');
  //const [contractorsList, setContractorsList] = useState<GenListType[]>([]);

  // context Data
  const myAppCtx = useContext(AppContext);
  const prerequisitesReady = arePrerequisitesReady(myAppCtx?.lookups);

  // Dashboard visibility is derived from permission-config (same source of truth as the ticket
  // details page), so the two can't drift out of sync. Every role is restricted to what it can view.
  const statusRestrictedRoles: Role[] = ['areaSignOff', 'finance', 'operationalReviewer', 'operator'];
  const ticketRole = getTicketRole(auth);
  const statusRoleRestricted = statusRestrictedRoles.includes(ticketRole);
  
  
  /****************************************************************************************
  // Status dropdown options for the current role. For operator these are the four collapsed
  // options (see permission-config), not the real statuses.
  ****************************************************************************************/
  const visibleTicketStatusList = useMemo(() => {
    const fullList = myAppCtx?.lookups.ticketStatusList || [];
    if (!statusRoleRestricted) return fullList;
    const searchableLookupKeys = getSearchableStatusLookupKeys(ticketRole);
    const searchableIds = TicketStatuses.filter(s => searchableLookupKeys.includes(s.lookupKey)).map(s => s.lookupId.toString());
    return fullList.filter(s => searchableIds.includes(s.value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myAppCtx?.lookups.ticketStatusList, statusRoleRestricted, ticketRole]);

  // Real status ids the current role can view a ticket in.
  const viewableStatusIds = useMemo(() => {
    const viewableLookupKeys = getViewableStatusLookupKeys(ticketRole);
    return TicketStatuses.filter(s => viewableLookupKeys.includes(s.lookupKey)).map(s => s.lookupId.toString());
  }, [ticketRole]);

  /****************************************************************************************
  // Build Query
  ****************************************************************************************/

  function buildQueryParams(): TicketsQueryParam {

    const crit: Partial<TicketsQueryParam> = { search: '' };
    // search string
    crit.search = searchStr.current;
    // Filters

    const _contractorNames = filters.current.contractorNames as GenListType[] || [];
    const _equipmentIds = filters.current.equipmentIds as GenListType[] || [];   // this is for equipment type ids 
    const _ticketStatus = filters.current.statusIds as GenListType[] || [0];
    const _forestAreas = filters.current.forestAreaIds as GenListType[] || [0];
    const _fireNumbers = filters.current.fireNos as GenListType[] || [];
    const _dateFrom = filters.current.dateFrom || null;
    const _dateTo = filters.current.dateTo || null;

    // The API expands each dropdown option (for operators, a collapsed category) to the real
    // statuses it stands for. Picking nothing (or every option) means "no narrower filter than
    // what this role can view" - for restricted roles we must still send the role's full viewable
    // status set rather than [], otherwise the backend paginates over every status in the system
    // and statuses this role can't view (e.g. Not Submitted) can push viewable ones (e.g. Submitted
    // for Area Sign Off) onto a later page before filterVisibleTickets ever gets a chance to show them.
    const selectedStatusIds = _ticketStatus.map((x: GenListType) => x.value);
    const noExplicitStatusFilter = selectedStatusIds.length === 0 || selectedStatusIds.length === visibleTicketStatusList.length;
    const effectiveStatusIds: string[] = noExplicitStatusFilter
      ? (statusRoleRestricted ? visibleTicketStatusList.map((s) => s.value) : [])
      : selectedStatusIds;

    crit.filter = {
      contractorNames:  (_contractorNames.length === 0  || _contractorNames.length === myAppCtx!.lookups.contractorsList.length) ?  [] : _contractorNames.map((x: GenListType) => x.value),
      equipmentIds:     (_equipmentIds.length === 0      || _equipmentIds.length   === myAppCtx!.lookups.equipmentTypeList.length) ?[] : _equipmentIds.map((x: GenListType) => x.value),
      statusIds:        effectiveStatusIds,
      forestAreaIds:    (_forestAreas.length === 0      ||   _forestAreas.length   === myAppCtx!.lookups.forestAreaList.length) ?   [] : _forestAreas.map((x: GenListType) => x.value),
      fireNos:    (_fireNumbers.length === 0) ? [] : _fireNumbers.map((x: GenListType) => x.value),
      dateFrom: _dateFrom,
      dateTo: _dateTo,

    };
    // pagination
    crit.paging = {
      sortCol: sorting.current.by,
      sortDir: sorting.current.dir,
      pageNo: pageNo.current === 0 ? 1 : pageNo.current,
      perPage: perPage.current,
    };
    //console.log(JSON.stringify(crit));
    return crit as TicketsQueryParam;
  }
  /****************************************************************************************
  // Load User call back function
  ****************************************************************************************/
  const loadTickets = useCallback((preview: boolean) => {
    if (!preview) {
      setProgressMsg("Loading tickets' information...");
    }
    // abort ongoing request
    abortControllers.forEach(controller => controller.abort());
    setAbortControllers([]);

    const queryParams = buildQueryParams();
    const controller = new AbortController();
    setAbortControllers([...abortControllers, controller]);

    ApiService.getFilteredTickets(auth.user?.access_token as string, queryParams, controller).then((res: any) => {
      if (res != null && res.items != null && Array.isArray(res.items)) {

        if (preview) {
          setPreviewTsList(res.items);
          previewTsListCount.current = res.pageInfo.total;
        }
        else {
          setMainTsList(res.items);
          mainTsListCount.current = res.pageInfo.total;
          exitPreviewMode();
        }
      } else {
        exitPreviewMode();
      }
    }).catch((error) => {
      appLog.debug(error);
      setProgressMsg('');
    }).finally(() => { setProgressMsg(''); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abortControllers]);

  /****************************************************************************************
  // Load initial tickets (called after prerequisites are loaded)
  ****************************************************************************************/
  const loadInitialTickets = () => {
    setProgressMsg("Loading tickets' information...");
    
    const queryParams = buildQueryParams();
    const controller = new AbortController();
    setAbortControllers([controller]);

    ApiService.getFilteredTickets(auth.user?.access_token as string, queryParams, controller).then((res: any) => {
      if (res != null && res.items != null && Array.isArray(res.items)) {
        setMainTsList(res.items);
        mainTsListCount.current = res.pageInfo.total;
        setIsInitialDataLoaded(true);
      } else {
        setIsInitialDataLoaded(true);
      }
    }).catch((error) => {
      appLog.debug(error);
      handleServerError(error, 'Failed to load tickets');
      setIsInitialDataLoaded(true); // Show UI even if tickets fail to load
    }).finally(() => { 
      setProgressMsg(''); 
    });
  };

  /****************************************************************************************
   * Load initial tickets once prerequisites are available from app-level context.
  ****************************************************************************************/
  useEffect(() => {
    // Guard 1: do nothing until authentication is ready and we have a token.
    if (!auth.isAuthenticated || !auth.user?.access_token) {
      return;
    }

    // Guard 2: prerequisites are loaded globally in App context; wait until they are ready.
    if (!prerequisitesReady) {
      return;
    }

    // Guard 3: initial ticket load should run only once per page lifecycle.
    if (isInitialDataLoaded) {
      return;
    }

    // All checks passed: load the dashboard ticket data.
    loadInitialTickets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.isAuthenticated,auth.user?.access_token,prerequisitesReady,isInitialDataLoaded]);

  /********************************************************************************************
   * 
  ********************************************************************************************/
  // useEffect(() => {
  //   // abort ongoing request
  //   abortControllers.forEach(controller => controller.abort());
  //   setAbortControllers([]);

  //   const controller = new AbortController();
  //   setAbortControllers([...abortControllers, controller]);

  //   // search string
  //   const qSearch = contractorNameInputValue;
  //   if (qSearch != null && qSearch.length > 0 && qSearch.trimStart() && qSearch.trimEnd()) {
  //     ApiService.loadContractors(auth.user?.access_token as string, qSearch).then((res: any) => {
  //       if (res != null) {
  //         const goodList:GenListType[] = res.map((x:string)=> ({label:x,value:x}));
  //         setContractorsList(goodList);
  //       }
  //     }).catch((error) => {
  //       console.log(error);
  //       navigate(`/ErrorPage/${error.status}`);
  //     }).finally(() => setProgressMsg(""));
  //   } else {
  //     setContractorsList([]);
  //   }
  // }, [contractorNameInputValue]);
  /**********************************************************************************
   * 
  ***********************************************************************************/
  function onFilterChange(who: String, value: any) {
    if (who === 'fltr_contractorName') {
      filters.current.contractorNames = value;
    }
    if (who === 'fltr_equipmentType') {
      filters.current.equipmentIds = value;
    }
    if (who === 'fltr_date_from') {

      let newVal = null;
      if ((value !== null) && !isNaN(value.toDate().getTime())) {
        newVal = (value ? value.format(dateFormat) : null);
      }
      filters.current.dateFrom = newVal as string;
    }
    if (who === 'fltr_date_to') {

      let newVal = null;
      if ((value !== null) && !isNaN(value.toDate().getTime())) {
        newVal = (value ? value.format(dateFormat) : null);
      }
      filters.current.dateTo = newVal as string;
    }
    if (who === 'fltr_status') {
      filters.current.statusIds = value;
    }
    if (who === 'fltr_forestArea') {
      filters.current.forestAreaIds = value;
    }
    if (who === 'fltr_fireNumber') {
      filters.current.fireNos = value;
    }
    pageNo.current = 1;
    loadTickets(false);
  }
  /****************************************************************************************
  *  get the data from the backend On Searching
  /****************************************************************************************/
  function OnSearchChange(value: string) {

    searchStr.current = value;
    if (searchStr.current.length === 0) {
      exitPreviewMode();
      sorting.current = { by: defSortCol, dir: defSortDir };
      loadTickets(false);

    } else if ((searchStr.current.length < minSearchChars)) {
      exitPreviewMode();
    } else if ((searchStr.current.length >= minSearchChars)) {
      pageNo.current = 1;
      loadTickets(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }
  /****************************************************************************************
   * Load fire numbers for autocomplete
  ****************************************************************************************/
  function loadFireNumbers(searchValue: string) {
    // abort ongoing request
    abortControllers.forEach(controller => controller.abort());
    setAbortControllers([]);

    const controller = new AbortController();
    setAbortControllers([...abortControllers, controller]);

    if (searchValue != null && searchValue.length > 0 && searchValue.trimStart() && searchValue.trimEnd()) {
      ApiService.queryFires(auth.user?.access_token as string, searchValue, controller).then((res: any) => {
        if (res != null && !wdpUtil.isNullorUndefined(res.data)) {
          const goodList: GenListType[] = res.data.map((elem: any) => { 
            const computedLabel = elem.fireNumber + ' - ' + elem.year?.toString();
            return { label: computedLabel, value:  computedLabel } 
          });
          setFireNumberOptions([...goodList]);
        }
      }).catch((error) => {
        if (error.name === "CanceledError") {
          // This is normal — ignore it 
          return;
        }
        appLog.debug(error);
        handleServerError(error, 'Failed to load fire numbers');
      });
    } else {
      setFireNumberOptions([]);
    }
  }
  /****************************************************************************************
   * styling after every rendering
  *****************************************************************************************/
  useEffect(() => {
    //  GoA styling 
    //let elem = document.getElementsByTagName getElementsByClassName('trailing-content')[0];
    // const page = document.getElementsByClassName('users-content')[0];
    // if (wdpUtil.isNullorUndefined(page))
    //   return;
    // var elems = page.querySelectorAll('[slot="trailingContent"]');
    // (elems[0] as HTMLElement).style.backgroundColor = 'transparent';// setAttribute("style","background-color:transparent");
    // (elems[0] as HTMLElement).style.borderWidth = '0px';
    // var wrap = searchWrap.current;
    // if (wrap !== undefined && wrap instanceof HTMLDivElement) {
    //   const w = wrap.clientWidth;
    //   // get search Inputfield position
    //   if (previewTableWrapElement.current) {
    //     if (previewTableWrapElement.current!.style.visibility === '' || previewTableWrapElement.current!.style.visibility === 'hidden') {
    //       //previewTableWrapElement.current!.style.left = `${Math.floor(wrap.clientLeft - 300)}px`;
    //       //previewTableWrapElement.current!.style.top = `${Math.floor(wrap.clientTop + 55)}px`;
    //       //previewTableWrapElement.current!.style.right = `0px`;
    //       //previewTableWrapElement.current!.style.bottom = `0px`;
    //       previewTableWrapElement.current!.style.width = `${Math.floor(w)}px`;
    //       previewTableWrapElement.current!.style.minWidth = `${Math.floor(w)}px`;
    //     }
    //   }
    //   //var inp = searchWrap.current.getElementsByTagName('goa-input')[0];
    // }
  });
  /****************************************************************************
   * 
   * @param newPage 
  ****************************************************************************/
  function OnPaginationChange(newPage: any) {
    // if (newPage >= 1) {
    //   const offset = (newPage - 1) * perPage;
    //   const _users = mainUsersList.slice(offset, offset + perPage)
    //   setPageNo(newPage);
    //   setPageUsers(_users);
    // }
    if (newPage >= 1) {
      //setPageNo(newPage);
      pageNo.current = newPage;
      loadTickets(false);
    }
  }
  /****************************************************************************
   * 
   * @param name 
   * @param value 
  ****************************************************************************/
  function changePerPage(_name: any, value: any) {
    const newPerPage = parseInt(value, 10);
    // const offset = (pageNo - 1) * newPerPage;
    // const _users = mainUsersList.slice(offset, offset + newPerPage);
    // setPageUsers(_users);
    //setPerPage(newPerPage);
    if (newPerPage >= 1) {

      // reset page No .back to 1
      pageNo.current = 1;


      perPage.current = newPerPage;
      loadTickets(false);
    }
  }
  /**********************************************************************************
   * @param sortBy 
   * @param sortDir 
  ***********************************************************************************/
  function sortTicketDataTable(tableId: any, param: GoabTableOnSortDetail) {
    if (tableId !== 'ticketsTable') {
      return;
    }
    const sortDir: number = param.sortDir;

    let sortBy: string  = param.sortBy;
    // col name mapping
    if(sortBy==='statusId'){
      sortBy = 'status';
    }
     if(sortBy==='equipmentId'){
      sortBy = 'equipment';
     }
    
    
    // ********************
    // NOTE: GoATable sortEvent goes to all table in the page. and this causing a big issue, becuase 
    // same event will go to User table and role table (the child table) 
    // as a workaround: added a prefix to the column name to filter sorting accordingly 
    // we are using the tableID to differentiate between tables SortEvent
    // ********************
    if (sortBy.includes("rolesTable")) {
      return;
    }
    // exec the sorting
    sorting.current = { by: sortBy, dir: sortDir === 1 ? "asc" : "desc" };
    // reset page No .back to 1
    pageNo.current = 1;
    loadTickets(false);

    // this is for debugging
    //const debug = _users.map((item) => {return item?.lastName});
    //console.log(debug);
  }

  /**********************************************************************************
   * Safety net: strip out any ticket whose status this role isn't allowed to view,
   * regardless of what the backend returned (e.g. before a filter is applied).
  ***********************************************************************************/
  function filterVisibleTickets(_tickets: any[]): any[] {
    if (!statusRoleRestricted) return _tickets;
    return _tickets.filter(ts => viewableStatusIds.includes(ts.statusId?.toString()));
  }

  /**********************************************************************************
   * 
   * @param users 
   * @returns 
  ***********************************************************************************/
  function populatetableData(_tickets: any[]): any[] {
    if (wdpUtil.isNullorUndefined(_tickets) || !Array.isArray(_tickets) || _tickets.length === 0) {
      return []
    }
    const visibleTickets = filterVisibleTickets(_tickets);
    const goodStatus = (ts: TicketInfo) => {
      const found = myAppCtx?.lookups.ticketStatusList.find(e => e.value.toString() === ts.statusId?.toString());
      if (!found) return "";
      // The API already reports the status as the caller is meant to see it.
      const label = found.label;
      //return (<span className={`${found?.label.includes("progress")? 'status_bg':''}`}>{found?.label}</span>);
      return <GoabBadge type={wdpUtil.includesIgnoreCase(label,"progress") ? "important" : "information"} content={label}></GoabBadge>
    }

    return (
      visibleTickets.map((ts, _i) =>
        <React.Fragment key={ts.digitalId + _i + '-Fragment'}>
          <tr key={ts.digitalId} id={ts.digitalId} data-testid={createTableTestId('tickets', 'row', ts.digitalId)}>
            <td style={{ textAlign: 'center' }}>
              <div
                onClick={(e) => { e.stopPropagation(); handleFlagClick(ts); }}
                style={{ cursor: 'pointer', display: 'inline-block', color: '#0070C4' }}
              >
                <GoabIcon type={ts.isFlagged ? "flag-filled" : "flag"} opacity={1}></GoabIcon>
              </div>
            </td>
            <td><Link to={`/ticket-ctrl/${ts.id}/${ts.ticketNumber}`} data-testid={createTableTestId('tickets', 'link', ts.digitalId)}><span>{ts.digitalId}</span></Link></td>
            <td>{ts.ticketNumber}</td>
            <td>{ts.date}</td>
            <td>{ts.contractNo}</td>
            <td>{ts.contractorName}</td>
            <td>{myAppCtx?.lookups.equipmentTypeList.find(e => e.value == ts.equipmentId?.toString())?.label}</td>
            <td>{ts.resourceCode}</td>
            <td>{myAppCtx?.lookups.forestAreaList.find(e => e.value == ts.forestAreaId.toString())?.label}</td>
            <td>{goodStatus(ts)}</td>
            

          </tr>
        </React.Fragment>
      )
    );
  }
  /**********************************************************************************
  ***********************************************************************************/

  /**********************************************************************************
   * 
  ***********************************************************************************/
  // function sortDataRoles(userId:string,tableId:any,param: GoabTableOnSortDetail) {
  //   if(tableId !== 'rolesTable'){
  //     return;
  //   }
  //   // sorting params
  //   const sortBy: string=param.sortBy;
  //   const sortDir: number=param.sortDir;

  //   //find the user
  //   // const newMainTsList = [...mainTsList];
  //   // const userObj = newMainTsList.find((x)=> x.id === userId);
  //   // if(wdpUtil.isNullorUndefined(userObj)){
  //   //   console.log(`sortData:Failed to find user for user Id (${userId})`);
  //   //   return;
  //   // }
  //   // //remove tablename prefix
  //   // const goodSortBy = sortBy.split('_');
  //   // if(goodSortBy==null || goodSortBy.length !== 2){
  //   //   return;
  //   // }
  //   // // exec the sorting
  //   // const _roles = wdpUtil.sortData(goodSortBy[1], sortDir, userObj?.grantedRoles);
  //   // // update the list
  //   // userObj!.grantedRoles = [..._roles];
  //   // setMainTs(newMainUsersList);
  // }
  /**********************************************************************************
   * 
  ***********************************************************************************/
  function populatePreviewtableData(): any[] {
    appLog.debug(previewTsList?.length);
    if (wdpUtil.isNullorUndefined(previewTsList) || !Array.isArray(previewTsList) || previewTsList.length === 0) {
      return []
    }
    return (
      filterVisibleTickets(previewTsList).map((ts, _i) =>
        <tr key={ts.ticketNumber + _i} data-testid={createSearchTestId(`preview-row-${_i}`)}>
          <td><Link to={`/ticket-ctrl/${ts.id}/${ts.ticketNumber}`} data-testid={createSearchTestId(`preview-link-${_i}`)}><span>{ts.digitalId}</span></Link></td>
          <td><span>{ts.contractorName}</span></td>
        </tr>
      )
    );
  }
  /***************************************************************************************
   * 
   * @param name 
   * @param value 
   **************************************************************************************/
  function onSearchInputChange(_name: string, value: string) {
    // event without a change in the content (special characters, arrows, ...)
    if (searchStr.current === value) {
      return;
    }
    OnSearchChange(value);
  }
  /****************************************************************************************
   * 
  *****************************************************************************************/
  function onSearchBtn() {
    exitPreviewMode();
    loadTickets(false);
  }
  /****************************************************************************************
   * 
  *****************************************************************************************/
  function onCloseSearchBtn() {
    exitPreviewMode();
    OnSearchChange('');
  }
  function exitPreviewMode() {
    setPreviewTsList([]);
    previewTsListCount.current = -1;
  }
  /****************************************************************************************
   * 
  *****************************************************************************************/
  function handleKeyEvent(event: any) {
    if (event.code === 'Enter' || event.code === 'NumpadEnter') {
      event.preventDefault();
      event.stopPropagation();
      event.nativeEvent.stopImmediatePropagation();
      onSearchBtn();
    }
  }
  /****************************************************************************************
   * 
  *****************************************************************************************/
  function populateSearchResTitle() {

    return (<div className='h3'>{previewTsListCount.current === -1 ? mainTsListCount.current : previewTsListCount.current} records found </div>);

    // if (searchStr && searchStr.current.length >= minSearchChars && !isLoading) {
    //   // search preview visible ?
    //   return (<div className='h3'>{previewTsListCount.current === -1? mainUsersListCount.current:previewTsListCount.current} result for '{searchStr.current}'</div>);
    //   /*}
    //   else{
    //     // we close the and reset the search preview (user clicked on the searech icon or hit enter)
    //     return (<div className='h3'>{mainUsersList?.length} result for '{searchStr}'</div>);
    //   }*/
    // }
    // else {
    //   return (<div></div>);
    // }
  }
  /****************************************************************************************
   * 
  *****************************************************************************************/
  function renderDropDown(label: string, id: string, val: GenListType[], options: any) {

    //const keyTypedVal   = who as typeof GenListType[];
    return (
      <GoabFormItem label={label} id={id}>
        <MultiSelect data-testid={id} className='multiSel-filter' closeOnChangedValue={false}
          options={options}
          value={val}
          onChange={(e: any) => onFilterChange(id, e)}
          labelledBy="--Select--"
        />
      </GoabFormItem>
    );
  }
  // function renderAutoComplete(label:string,who:string,fldVal:GenListType[],options:GenListType[],placeholder:string){
  //   const width="100%";
  //   return (
  //   <GoabFormItem label={label} id={who}>
  //     <Autocomplete className='formField' multiple disableCloseOnSelect includeInputInList filterSelectedOptions /*freeSolo*/ autoComplete  id={who} data-testid={`tic_${who}`} sx={{ width }} 
  //       noOptionsText="No Contractor name"
  //       getOptionLabel={(option) => typeof option === 'string' ? option : option.label}
  //       renderInput={(params) => <TextField {...params} label="" placeholder={placeholder} />}
  //       filterOptions={(x) => x}
  //       options={options}
  //       value={fldVal}
  //       onChange={(_event: any, value: GenListType[] | null) => { onFilterChange(who, value) }}
  //       inputValue={contractorNameInputValue}
  //       onInputChange={(_event, newInputValue) => { setContractorNameInputValue(newInputValue) }}
  //       //onBlur={(e: any) => onMyBlur(e?.nativeEvent?.target?.id, e?.nativeEvent?.target?.value)}
  //       renderOption={(props, option, { selected }) => (
  //       <li {...props}><Checkbox style={{ marginRight: 8 }} checked={selected}/><ListItemText primary={option.label} /></li>
  //     )}
  //     />
  //   </GoabFormItem>
  //   );
  // }
  /****************************************************************************************
   * 
  *****************************************************************************************/
  function renderDateFilter(who: string, label: string, val: any) {
    let aDt = (val ? dayjs(val as string).endOf('day') : null);
    return (
      <GoabFormItem label={label} id={who}>
        <div className='formField date-field'
        //onBlur={() => { onMyBlur(who, fldVal) }}
        >
          <CustomFmF id={who} name={label} data-testid={who}
            value={aDt}
            onChange={(e: any) => onFilterChange(who, e?.target?.value)}
          />
        </div>
      </GoabFormItem>
    );
  }
  /****************************************************************************************
   * Render fire number filter with TextMultiAutocomplete component
  *****************************************************************************************/
  function renderFireNumberFilter() {
    const selectedValues = (filters.current.fireNos as GenListType[]) || [];
    
    return (
      <GoabFormItem label="Fire number" id="fltr_fireNumber">
        <TextMultiAutocomplete<GenListType>
          data-testid="fltr_fireNumber"
          options={fireNumberOptions}
          value={selectedValues}
          onChange={(value) => onFilterChange('fltr_fireNumber', value)}
          getOptionLabel={(option) => option.label ?? ""}
          getOptionKey={(option) => option.value}
          placeholder="--type to select--"
          inputValue={fireNumberInputValue}
          onInputChange={(newValue) => {
            setFireNumberInputValue(newValue);
            loadFireNumbers(newValue);
          }}
          height={40}
          minInputChars={6}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'background.paper',
            }
          }}
        />
      </GoabFormItem>
    );
  }
  /****************************************************************************************
   * 
  *****************************************************************************************/
  // function renderDateRangeFilter(who:string,label:string,val:any) {
  //   let aDt = (val ? dayjs(val as string).endOf('day') : null);
  //   return (
  //     <GoabFormItem label={label} id={who}>
  //       <DateRangePicker id={who} name={label}
  //         value={aDt}
  //         onChange={(e: any) => onFilterChange(who, e?.target?.value)}
  //       />
  //     </GoabFormItem>
  //   );
  // }


  /****************************************************************************************
   * 
  *****************************************************************************************/
  // function onBlurRow(e:any):void{
  //   const userObj = inPlaceEditUser; //easy access 
  //   userObj!.errors.accessStartDate = wdpUtil.validateStartDate (userObj?.accessStartDate?.toString(),userObj?.accessEndDate?.toString());
  //   userObj!.errors.accessEndDate   = wdpUtil.validateEndDate   (userObj?.accessStartDate?.toString(),userObj?.accessEndDate?.toString());
  // }
  /****************************************************************************************
   * Handle flag toggle for a ticket from the dashboard
  *****************************************************************************************/
  const handleFlagClick = (ticket: TicketInfo) => {
    if (!ticket.id) return;

    const currentFlagState = ticket.isFlagged;
    const apiCall = currentFlagState
      ? ApiService.unflagTicket(auth.user?.access_token as string, ticket.id)
      : ApiService.flagTicket(auth.user?.access_token as string, ticket.id);
    setProgressMsg("Updating flag...");

    apiCall
      .then(_res => {
        // Update local state in main list
        const updatedMainList = mainTsList.map(ts =>ts.id === ticket.id ? { ...ts, isFlagged: !currentFlagState } : ts);
        setMainTsList(updatedMainList);

        // Update preview list if it's active
        if (previewTsList.length > 0) {
          const updatedPreviewList = previewTsList.map(ts =>ts.id === ticket.id ? { ...ts, isFlagged: !currentFlagState } : ts);
          setPreviewTsList(updatedPreviewList);
        }

        const message = currentFlagState ? "Flag removed from time ticket." : "Time ticket flagged for review.";
        handleToastOpen(message, "success");
      })
      .catch((error) => {
        handleServerError(error, 'Failed to update flag status');
      })
      .finally(() => setProgressMsg(""));
  };
  /****************************************************************************************
  *****************************************************************************************/
  const handleServerError = (error: any, operationMessage?: string) => {
    appLog.error('Server error:', error);
    let errorMessage = operationMessage || 'Operation has failed';
    
    if (error?.status >= 400 && error?.status < 500 && error?.message) {
      errorMessage = error?.response?.data?.detail || error.message || errorMessage;
    }
    
    handleToastOpen(errorMessage, 'emergency');
  };
  /****************************************************************************************
  *****************************************************************************************/
  const handleToastOpen = (_msg: string, _type: GoabCalloutType = "success") => {
    // GoabCalloutType -> GoabTemporaryNotificationType: success->success, emergency->failure, everything else->basic
    const notificationType: GoabTemporaryNotificationType = _type === 'success' ? 'success' : _type === 'emergency' ? 'failure' : 'basic';
    TemporaryNotification.show(_msg, { type: notificationType, duration: 'short' });
  };
  //////////////////////////////////////////////////////////////////////////////////////////
  // html part
  return (
    <div className='timesheets-content'>
      {!isInitialDataLoaded ? (
        <GoabCircularProgress variant="fullscreen" size="large" message={progressMsg || "Loading"} visible={true} />
      ) : (
        <>
      {/************** Title Section *************/}
      <section className='title-section'>
        <h2 data-testid="dashboard-title" className='title'>Fireline Equipment Time Tickets</h2>
        {canShowButton(null, 'create', undefined, getTicketRole(auth)) && <GoabButton data-testid="edit-ticket-btn" type='primary' size='compact' onClick={() => navigate(`/ticket-ctrl/new`)}>Create new ticket +</GoabButton>}
      </section>

      {/************** Filter Section *************/}
      <section className='search-section filter-grid'>
        <div id="fltr_contractorName" data-testid="fltr_contractorName">
          {/* {renderAutoComplete("Contractor Name","fltr_contractorName",(filters.current.contractorNames as GenListType[])!,(contractorsList ),"Select contractors")} */}
          {/* (myAppCtx?.contractorsList as []).map((x:GenListType)=> ({label:x.label,value:x.value})).filter((obj, index, self) => index === self.findIndex((t) => t.value === obj.value)))} */}
          {renderDropDown("Contractor name", "fltr_contractorName", (filters.current.contractorNames as GenListType[]), myAppCtx?.lookups.contractorsList)}
        </div>
        <div id="fltr_equipmentType" data-testid="fltr_equipmentType">
          {renderDropDown("Equipment type", "fltr_equipmentType", (filters.current.equipmentIds as GenListType[]), myAppCtx?.lookups.equipmentTypeList)}
        </div>
        <div id="fltr_date_from" data-testid="fltr_date_from">
          {renderDateFilter("fltr_date_from", "From", filters.current.dateFrom!)}
        </div>
        <div id="fltr_date_to" data-testid="fltr_date_to">
          {renderDateFilter("fltr_date_to", "To", filters.current.dateTo!)}
        </div>

        <div id="fltr_forestArea" data-testid="fltr_forestArea">
          {renderDropDown("Forest area", "fltr_forestArea", (filters.current.forestAreaIds as GenListType[])!, myAppCtx?.lookups.forestAreaList)}
        </div>

        <div id="fltr_fireNumber" data-testid="fltr_fireNumber">
          {renderFireNumberFilter()}
        </div>

        <div id="fltr_status" data-testid="fltr_status">
          {renderDropDown("Status", "fltr_status", (filters.current.statusIds as GenListType[])!, visibleTicketStatusList)}
        </div>

        <div className='search-section-end'>
          <div className='txt-search-wrap' ref={searchWrap} onKeyDown={handleKeyEvent}>
            <div className='search-input'>
              <TextField type='text' name="inc-dash-search" data-testid="inc-search"
                placeholder="Search by FP167"
                autoComplete='off'
                value={searchStr.current}
                onChange={(e: any) => onSearchInputChange(e.target.name, e.target.value)}
              />
            </div>
            <div className='search-buttons-wrap'>
              {(searchStr === null || searchStr.current.length === 0) ? <GoabIconButton data-testid={createButtonTestId('search')} icon="search" variant="dark" size="medium" disabled title="Search" onClick={function (): void { onSearchBtn() }} /> : <GoabIconButton data-testid={createButtonTestId('search')} icon="search" variant="dark" size="medium" title="Search" onClick={function (): void { onSearchBtn() }} />}
              {(searchStr === null || searchStr.current.length === 0) ? <div style={{ width: '26px' }}></div> : <GoabIconButton data-testid={createButtonTestId('close-search')} icon="close" variant="dark" size="medium" title="Close" onClick={function (): void { onCloseSearchBtn() }} />}
            </div>
            {(previewTsListCount.current !== -1 && previewTsList.length) ?
              <div className='search-preview-wrap' ref={previewTableWrapElement}>
                <GoabTable key="search-preview-table" data-testid={createSearchTestId('preview-table')} width="100%" mb="xl">
                  <thead></thead>
                  <tbody >{populatePreviewtableData()}</tbody>
                </GoabTable>
              </div>
              : <div></div>
            }
          </div>
        </div>
      </section>
      {mainTsListCount.current >= 0 && (
        <section className='search-result-section'>
          {
            populateSearchResTitle()
          }
        </section>
      )}
      {/************ Data Table Section ***********/}
      {/* {(!wdpUtil.isNullorUndefined(mainUsersList) && mainUsersList.length) ? */}
      {(!wdpUtil.isNullorUndefined(myAppCtx?.lookups.equipmentTypeList) && myAppCtx?.lookups.equipmentTypeList.length) && <div>
        <section className="table-section ticket-table">
          <div className="ticket-table-inner">
          <GoabTable testId={createTableTestId('tickets', 'main')} width="100%" mb="xl" onSort={(param: GoabTableOnSortDetail) => sortTicketDataTable('ticketsTable', param)}>
            <thead>
              <tr>
                <th className="col-flag" data-testid={createTableTestId('tickets', 'header', 'isFlagged')}>
                  <GoabTableSortHeader name="isFlagged" direction="none" data-testid={createTableTestId('tickets', 'sort-header', 'isFlagged')}><span></span></GoabTableSortHeader>
                </th>
                <th className="col-digitalId" data-testid={createTableTestId('tickets', 'header', 'digitalId')}>
                  <GoabTableSortHeader name="digitalId" direction="none" data-testid={createTableTestId('tickets', 'sort-header', 'digitalId')}><span>Digital ID</span></GoabTableSortHeader>
                </th>
                <th className="col-ticketNumber" data-testid={createTableTestId('tickets', 'header', 'ticketNumber')}>
                  <GoabTableSortHeader name="ticketNumber" direction="none" data-testid={createTableTestId('tickets', 'sort-header', 'ticketNumber')}><span>FP167 Number</span></GoabTableSortHeader>
                </th>
                <th className="col-date" data-testid={createTableTestId('tickets', 'header', 'date')}>
                  <GoabTableSortHeader name="date" direction={defSortDir} data-testid={createTableTestId('tickets', 'sort-header', 'date')}><span>Date</span></GoabTableSortHeader>
                </th>
                <th className="col-contractNo" data-testid={createTableTestId('tickets', 'header', 'contractNo')}>
                  <GoabTableSortHeader name="contractNo" direction="none" data-testid={createTableTestId('tickets', 'sort-header', 'contractNo')}><span>Contract number</span></GoabTableSortHeader>
                </th>
                <th className="col-contractorName" data-testid={createTableTestId('tickets', 'header', 'ContractorName')}>
                  <GoabTableSortHeader name="ContractorName" direction="none" data-testid={createTableTestId('tickets', 'sort-header', 'ContractorName')}><span>Contractor name</span></GoabTableSortHeader>
                </th>
                <th className="col-equipmentId" data-testid={createTableTestId('tickets', 'header', 'equipmentId')}>
                  <GoabTableSortHeader name="equipmentId" direction="none" data-testid={createTableTestId('tickets', 'sort-header', 'equipmentId')}><span>Equipment type and sub-type</span></GoabTableSortHeader>
                </th>
                <th className="col-resourceCode" data-testid={createTableTestId('tickets', 'header', 'resourceCode')}>
                  <GoabTableSortHeader name="resourceCode" direction="none" data-testid={createTableTestId('tickets', 'sort-header', 'resourceCode')}><span>Unit number</span></GoabTableSortHeader>
                </th>
                <th className="col-forestArea" data-testid={createTableTestId('tickets', 'header', 'forestArea')}>
                  <GoabTableSortHeader name="forestArea" direction="none" data-testid={createTableTestId('tickets', 'sort-header', 'forestArea')}><span>Forest area</span></GoabTableSortHeader>
                </th>
                <th className="col-statusId" data-testid={createTableTestId('tickets', 'header', 'statusId')}>
                  <GoabTableSortHeader name="statusId" direction="none" data-testid={createTableTestId('tickets', 'sort-header', 'statusId')}><span>Status</span></GoabTableSortHeader>
                </th>
              </tr>
            </thead>
            <tbody>
              {populatetableData(mainTsList)}
            </tbody>
          </GoabTable>
          </div>
        </section>
        {(wdpUtil.isNullorUndefined(mainTsList) != null && mainTsList.length > 0) &&
          <GoabBlock alignment="center">
            <GoabBlock mb="m" alignment="center" gap="m">
              <span>Show</span>
              <GoabDropdown testId={createPageTestId('pagination-per-page')} onChange={(details: GoabDropdownOnChangeDetail) => { changePerPage(details.name, details.value) }} value="10" width="8ch" size="compact">
                <GoabDropdownItem value="10" label="10" data-testid={createPageTestId('per-page-10')} />
                <GoabDropdownItem value="15" label="15" data-testid={createPageTestId('per-page-15')} />
                <GoabDropdownItem value="20" label="20" data-testid={createPageTestId('per-page-20')} />
                <GoabDropdownItem value="50" label="50" data-testid={createPageTestId('per-page-50')} />
                <GoabDropdownItem value="100" label="100" data-testid={createPageTestId('per-page-100')} />
              </GoabDropdown>
              <span style={{ whiteSpace: 'nowrap' }}>of {mainTsListCount.current} items</span>
            </GoabBlock>
            <GoabSpacer hSpacing="fill" />
            <GoabPagination testId={createPageTestId('pagination')}
              variant={'all'}
              itemCount={mainTsListCount.current}
              perPageCount={perPage.current}
              pageNumber={pageNo.current}
              onChange={(details: GoabPaginationOnChangeDetail) => { OnPaginationChange(details.page) }}
            />
          </GoabBlock>
        }
      </div>
      }

      {/* Circular Progress as a layer  must be child of the page div */}
      {(progressMsg !== "" && (!props.testing)) && <GoabCircularProgress variant="fullscreen" size="large" message={progressMsg} visible={true} />}

      </>
      )}
    </div>

  );

}